import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Alert,
  Snackbar
} from '@mui/material';
import {
  LocationOn,
  AccessTime,
  AttachMoney,
  CalendarToday,
  Share,
  Favorite,
  FavoriteBorder,
  WhatsApp,
  Check,
  Close,
  Info,
  ShoppingBag,
  Star,
  Login,
  CurrencyLira
} from '@mui/icons-material';
import { tourService, bookingService } from '../services/firebase';
import { Tour } from '../types';
import { useAuth } from '../contexts/AuthContext';
import WhatsAppButton from '../components/Common/WhatsAppButton';
import { Timestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../utils/getTranslatedData';

const localeMap: { [key: string]: string } = {
  tr: 'tr-TR',
  en: 'en-GB',
  ar: 'ar-SA'
};

const currencyMap: { [key: string]: string } = {
  tr: 'TRY',
  en: 'TRY',
  ar: 'TRY'
};

const currencySymbolMap: { [key: string]: string } = {
  tr: '₺',
  en: '₺',
  ar: '₺'
};

const TourDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { t, i18n } = useTranslation();
  const [tour, setTour] = useState<Tour | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    people: 1,
    specialRequests: ''
  });

  useEffect(() => {
    if (id) {
      fetchTour();
    }
  }, [id]);

  useEffect(() => {
    if (tour?.images && tour.images.length > 1) {
      const interval = setInterval(() => {
        setSelectedImage((prev) => (prev + 1) % tour.images.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [tour?.images]);

  const fetchTour = async () => {
    try {
      setLoading(true);
      setError(null);
      if (id) {
        const tourData = await tourService.getTour(id);
        if (tourData) {
          setTour(tourData);
        } else {
          setError(t('tourDetail.tourNotFound'));
        }
      }
    } catch (err) {
      console.error('Error fetching tour:', err);
      setError(t('tourDetail.tourLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = () => {
    // Artık kayıt olmadan da rezervasyon yapılabilir
    setBookingDialogOpen(true);
  };

  const handleCloseBooking = () => {
    setBookingDialogOpen(false);
    setBookingForm({
      name: '',
      email: '',
      phone: '',
      people: 1,
      specialRequests: ''
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleFormChange = (field: string, value: string | number) => {
    setBookingForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitBooking = async () => {
    if (!tour) return;
    
    // Form validasyonu
    if (!bookingForm.name || !bookingForm.email || !bookingForm.phone) {
      setSnackbarMessage(t('tourDetail.formValidationError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      setSnackbarMessage(t('tourDetail.savingBooking'));
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      
      const bookingData: any = {
        tourId: tour.id,
        tourName: getTranslatedData(tour.name, i18n.language),
        customerName: bookingForm.name,
        customerEmail: bookingForm.email,
        customerPhone: bookingForm.phone,
        bookingDate: Timestamp.now(),
        tourDate: Timestamp.fromDate(new Date(tour.startDate)),
        participants: bookingForm.people,
        totalPrice: tour.price * bookingForm.people,
        currency: currencyMap[i18n.language] || 'TRY',
        status: 'pending' as const,
        paymentStatus: 'pending' as const,
        specialRequests: bookingForm.specialRequests || '',
        tourDetails: {
          destination: getTranslatedData(tour.destination, i18n.language),
          duration: `${tour.duration} ${t('days', { count: tour.duration })}`
        }
      };

      // Sadece kullanıcı giriş yapmışsa userId ekle
      if (currentUser?.uid) {
        bookingData.userId = currentUser.uid;
      }
      
      await bookingService.createBooking(bookingData);
      
      const redirectMessage = currentUser 
        ? t('tourDetail.redirectToBookings') 
        : t('tourDetail.redirectToHome');
      setSnackbarMessage(t('tourDetail.bookingSuccess', { redirectMessage }));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCloseBooking();
      
      // Kullanıcı giriş yapmışsa rezervasyonlarım sayfasına, yapmamışsa ana sayfaya yönlendir
      setTimeout(() => {
        if (currentUser) {
          navigate('/booking-history');
        } else {
          navigate('/');
        }
      }, 3000);
    } catch (err: any) {
      console.error('Rezervasyon kaydetme hatası:', err);
      setSnackbarMessage(t('tourDetail.bookingError', { error: err.message || t('unknownError') }));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    if (bookingDialogOpen && currentUser) {
      // Kullanıcı giriş yapmışsa bilgilerini otomatik doldur
      const displayName = userProfile?.displayName || currentUser.displayName || '';
      const email = userProfile?.email || currentUser.email || '';
      const phone = userProfile?.phoneNumber || currentUser.phoneNumber || '';
      
      setBookingForm(prev => ({
        ...prev,
        name: displayName,
        email: email,
        phone: phone
      }));
    } else if (bookingDialogOpen) {
      // Kullanıcı giriş yapmamışsa formu temizle
      setBookingForm({
        name: '',
        email: '',
        phone: '',
        people: 1,
        specialRequests: ''
      });
    }
  }, [bookingDialogOpen, currentUser, userProfile]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const currentLocale = localeMap[i18n.language] || 'tr-TR';
    return date.toLocaleDateString(currentLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: 'var(--light-gray)'
      }}>
        <CircularProgress sx={{ color: 'var(--light-blue-cta)' }} />
      </Box>
    );
  }

  if (error || !tour) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: 'var(--light-gray)'
      }}>
        <Card sx={{ 
          p: 4, 
          textAlign: 'center',
          backgroundColor: 'white',
          boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
          borderRadius: 3
        }}>
          <Typography variant="h4" sx={{ color: 'var(--navy-blue)', fontWeight: 700 }} gutterBottom>
            {error || t('tourDetail.tourNotFound')}
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/tours')}
            sx={{ 
              mt: 2,
              backgroundColor: 'var(--light-blue-cta)',
              borderRadius: 2,
              textTransform: 'none',
              px: 4,
              '&:hover': {
                backgroundColor: 'var(--cobalt-blue)'
              }
            }}
          >
            {t('tourDetail.backToTours')}
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{tour ? getTranslatedData(tour.name, i18n.language) : t('tourDetail.pageTitle')} - HatTravel</title>
        <meta name="description" content={getTranslatedData(tour.description, i18n.language)} />
      </Helmet>
      
      <Box sx={{ 
        backgroundColor: 'var(--light-gray)',
        minHeight: '100vh'
      }}>
        {/* Image Gallery - Full Width */}
        {tour.images && tour.images.length > 0 && (
          <Box sx={{ position: 'relative', height: '60vh', minHeight: 400, overflow: 'hidden' }}>
            {tour.images.map((image, index) => (
              <Box
                key={index}
                component="img"
                src={image}
                alt={`${tour.name} ${index + 1}`}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: selectedImage === index ? 1 : 0,
                  transition: 'opacity 1.5s ease-in-out',
                  transform: selectedImage === index ? 'scale(1)' : 'scale(1.05)',
                  filter: selectedImage === index ? 'brightness(1)' : 'brightness(0.8)',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    transition: 'transform 0.3s ease-in-out'
                  }
                }}
              />
            ))}
            
            {/* Image Overlay */}
            <Box sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(transparent, rgba(1, 22, 65, 0.8))',
              p: 4,
              color: 'white',
              zIndex: 2
            }}>
              <Typography variant="h2" sx={{ 
                fontWeight: 700, 
                fontSize: { xs: '2rem', md: '3.5rem' },
                mb: 2,
                opacity: 1,
                transition: 'opacity 0.5s ease-in-out'
              }}>
                {getTranslatedData(tour.name, i18n.language)}
              </Typography>
              
              {/* Tour Info Chips */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Chip
                  icon={<LocationOn />}
                  label={getTranslatedData(tour.destination, i18n.language)}
                  sx={{ 
                    backgroundColor: 'rgba(248, 249, 250, 0.2)',
                    color: 'white',
                    border: '1px solid rgba(248, 249, 250, 0.3)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(248, 249, 250, 0.3)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                />
                <Chip
                  icon={<AccessTime />}
                  label={`${tour.duration} ${t('days', { count: tour.duration })}`}
                  sx={{ 
                    backgroundColor: 'rgba(248, 249, 250, 0.2)',
                    color: 'white',
                    border: '1px solid rgba(248, 249, 250, 0.3)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(248, 249, 250, 0.3)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                />
                <Chip
                  icon={<CurrencyLira />}
                  label={`${tour.price.toLocaleString(localeMap[i18n.language] || 'tr-TR')} ${currencySymbolMap[i18n.language] || '₺'}`}
                  sx={{ 
                    backgroundColor: 'rgba(248, 249, 250, 0.2)',
                    color: 'white',
                    border: '1px solid rgba(248, 249, 250, 0.3)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(248, 249, 250, 0.3)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Image Navigation Dots */}
            {tour.images.length > 1 && (
              <Box sx={{
                position: 'absolute',
                bottom: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 1.5,
                zIndex: 3
              }}>
                {tour.images.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: selectedImage === index ? 16 : 12,
                      height: selectedImage === index ? 16 : 12,
                      borderRadius: '50%',
                      backgroundColor: selectedImage === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
                      cursor: 'pointer',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: selectedImage === index ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
                      '&:hover': {
                        backgroundColor: selectedImage === index ? 'white' : 'rgba(255, 255, 255, 0.7)',
                        transform: 'scale(1.2)'
                      }
                    }}
                    onClick={() => setSelectedImage(index)}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
        

        <Container maxWidth="lg">
          {/* Action Buttons */}
          
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            mb: 6, 
            mt: 4,
            justifyContent: 'center'
          }}>
            <IconButton 
              onClick={() => setIsFavorite(!isFavorite)}
              sx={{ 
                backgroundColor: 'white',
                border: '1px solid var(--gray-shadow)',
                borderRadius: 2,
                width: 56,
                height: 56,
                boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                '&:hover': { 
                  backgroundColor: 'var(--light-gray)',
                  transform: 'scale(1.05)'
                }
              }}
            >
              {isFavorite ? <Favorite sx={{ color: '#e91e63' }} /> : <FavoriteBorder sx={{ color: 'var(--navy-blue)' }} />}
            </IconButton>
            <IconButton 
              sx={{ 
                backgroundColor: 'white',
                border: '1px solid var(--gray-shadow)',
                borderRadius: 2,
                width: 56,
                height: 56,
                boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                '&:hover': { 
                  backgroundColor: 'var(--light-gray)',
                  transform: 'scale(1.05)'
                }
              }}
            >
              <Share sx={{ color: 'var(--navy-blue)' }} />
            </IconButton>
          </Box>

          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, 
            gap: 6,
            pb: 6
          }}>
            {/* Main Content */}
            <Box>
              {/* Description */}
              <Card sx={{ 
                mb: 6, 
                borderRadius: 3,
                backgroundColor: 'white',
                boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
              }}>
                <CardContent sx={{ p: 5 }}>
                  <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 4, color: 'var(--navy-blue)' }}>
                    {t('tourDetail.descriptionTitle')}
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: '1.1rem', color: 'var(--navy-blue)', fontWeight: 400 }}>
                    {getTranslatedData(tour.description, i18n.language)}
                  </Typography>
                </CardContent>
              </Card>

              {/* Highlights */}
              {tour.highlights && (
                <Card sx={{ 
                  mb: 6, 
                  borderRadius: 3,
                  backgroundColor: 'white',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
                }}>
                  <CardContent sx={{ p: 5 }}>
                    <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 4, color: 'var(--navy-blue)' }}>
                      {t('tourDetail.highlightsTitle')}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr' }, gap: 3 }}>
                      {(getTranslatedData(tour.highlights, i18n.language) || '').split('•').filter((item: string) => item.trim()).map((highlight: string, index: number) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Star sx={{ fontSize: 24, color: 'var(--light-blue-cta)' }} />
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'var(--navy-blue)', fontWeight: 400 }}>{highlight.trim()}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Itinerary */}
              {tour.itinerary && (
                <Card sx={{ 
                  mb: 6, 
                  borderRadius: 3,
                  backgroundColor: 'white',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
                }}>
                  <CardContent sx={{ p: 5 }}>
                    <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 4, color: 'var(--navy-blue)' }}>
                      {t('tourDetail.itineraryTitle')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {(getTranslatedData(tour.itinerary, i18n.language) || '').split(/\d+\.\s*Gün:|Day\s*\d+:/i).filter((item: string) => item.trim()).map((day: string, index: number) => (
                        <Paper key={index} sx={{ 
                          p: 4, 
                          backgroundColor: 'var(--light-gray)',
                          borderRadius: 3,
                          border: '1px solid var(--gray-shadow)',
                          boxShadow: 'none'
                        }}>
                          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'var(--light-blue-cta)' }}>
                            {t('tourDetail.day', { count: index + 1 })}
                          </Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'var(--navy-blue)', fontWeight: 400, whiteSpace: 'pre-wrap' }}>
                            {day.trim()}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Included & Not Included */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4, mb: 6 }}>
                {/* Included */}
                {tour.included && (
                  <Card sx={{ 
                    height: '100%', 
                    borderRadius: 3,
                    backgroundColor: 'white',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4, display: 'flex', alignItems: 'center', gap: 3, color: 'var(--navy-blue)' }}>
                        <Check sx={{ color: '#27ae60' }} />
                        {t('tourDetail.includedTitle')}
                      </Typography>
                      <List sx={{ p: 0 }}>
                        {(getTranslatedData(tour.included, i18n.language) || '').split('•').filter((item: string) => item.trim()).map((item: string, index: number) => (
                          <ListItem key={index} sx={{ px: 0, py: 1.5 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <Check sx={{ fontSize: 20, color: '#27ae60' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={item.trim()} 
                              sx={{ 
                                '& .MuiListItemText-primary': { 
                                  fontSize: '1rem',
                                  color: 'var(--navy-blue)',
                                  fontWeight: 400
                                } 
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                )}

                {/* Not Included */}
                {tour.notIncluded && (
                  <Card sx={{ 
                    height: '100%', 
                    borderRadius: 3,
                    backgroundColor: 'white',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4, display: 'flex', alignItems: 'center', gap: 3, color: 'var(--navy-blue)' }}>
                        <Close sx={{ color: '#e74c3c' }} />
                        {t('tourDetail.notIncludedTitle')}
                      </Typography>
                      <List sx={{ p: 0 }}>
                        {(getTranslatedData(tour.notIncluded, i18n.language) || '').split('•').filter((item: string) => item.trim()).map((item: string, index: number) => (
                          <ListItem key={index} sx={{ px: 0, py: 1.5 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <Close sx={{ fontSize: 20, color: '#e74c3c' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={item.trim()} 
                              sx={{ 
                                '& .MuiListItemText-primary': { 
                                  fontSize: '1rem',
                                  color: 'var(--navy-blue)',
                                  fontWeight: 400
                                } 
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                )}
              </Box>

              {/* Preparation */}
              {tour.preparation && (
                <Card sx={{ 
                  mb: 6, 
                  borderRadius: 3,
                  backgroundColor: 'white',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
                }}>
                  <CardContent sx={{ p: 5 }}>
                    <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 4, display: 'flex', alignItems: 'center', gap: 3, color: 'var(--navy-blue)' }}>
                      <Info sx={{ color: 'var(--light-blue-cta)' }} />
                      {t('tourDetail.preparationTitle')}
                    </Typography>
                    <List sx={{ p: 0 }}>
                      {(getTranslatedData(tour.preparation, i18n.language) || '').split('•').filter((item: string) => item.trim()).map((item: string, index: number) => (
                        <ListItem key={index} sx={{ px: 0, py: 1.5 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Info sx={{ fontSize: 20, color: 'var(--light-blue-cta)' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item.trim()} 
                            sx={{ 
                              '& .MuiListItemText-primary': { 
                                fontSize: '1rem',
                                color: 'var(--navy-blue)',
                                fontWeight: 400
                              } 
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}

              {/* Souvenirs */}
              {tour.souvenirs && (
                <Card sx={{ 
                  mb: 6, 
                  borderRadius: 3,
                  backgroundColor: 'white',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
                }}>
                  <CardContent sx={{ p: 5 }}>
                    <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 4, display: 'flex', alignItems: 'center', gap: 3, color: 'var(--navy-blue)' }}>
                      <ShoppingBag sx={{ color: 'var(--cobalt-blue)' }} />
                      {t('tourDetail.souvenirsTitle')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {(getTranslatedData(tour.souvenirs, i18n.language) || '').split('•').filter((item: string) => item.trim()).map((item: string, index: number) => (
                        <Chip
                          key={index}
                          label={item.trim()}
                          variant="outlined"
                          sx={{ 
                            borderRadius: 2,
                            fontSize: '1rem',
                            backgroundColor: 'white',
                            border: '1px solid var(--gray-shadow)',
                            color: 'var(--navy-blue)',
                            '&:hover': { 
                              backgroundColor: 'var(--light-gray)',
                              borderColor: 'var(--cobalt-blue)'
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>

            {/* Sidebar */}
            <Box>
              {/* Price Card */}
              <Card sx={{ 
                mb: 4, 
                position: 'sticky', 
                top: 20, 
                backgroundColor: 'white',
                borderRadius: 3,
                border: '1px solid var(--gray-shadow)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
              }}>
                <CardContent sx={{ p: 5 }}>
                  <Typography variant="h2" gutterBottom sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center', 
                    color: 'var(--navy-blue)'
                  }}>
                    {tour.price.toLocaleString(localeMap[i18n.language] || 'tr-TR')} {currencySymbolMap[i18n.language] || '₺'}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'var(--navy-blue)', opacity: 0.7, textAlign: 'center', mb: 4, fontSize: '1.1rem' }}>
                    {t('tourDetail.perPerson')}
                  </Typography>
                  
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleBooking}
                    startIcon={currentUser ? <ShoppingBag /> : <Login />}
                    sx={{ 
                      mb: 4, 
                      py: 2.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1.2rem',
                      backgroundColor: 'var(--light-blue-cta)',
                      fontWeight: 600,
                      '&:hover': { 
                        backgroundColor: 'var(--cobalt-blue)'
                      }
                    }}
                  >
                    {t('tourDetail.bookNow')}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<WhatsApp />}
                    onClick={() => window.open(`https://wa.me/905551234567?text=${encodeURIComponent(t('tourDetail.whatsappMessage', { tourName: getTranslatedData(tour.name, i18n.language) }))}`, '_blank')}
                    sx={{ 
                      py: 2.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      borderColor: 'var(--gray-shadow)',
                      color: 'var(--navy-blue)',
                      borderWidth: 1,
                      '&:hover': { 
                        borderColor: 'var(--cobalt-blue)',
                        backgroundColor: 'var(--light-gray)',
                        borderWidth: 1
                      }
                    }}
                  >
                    {t('tourDetail.contactViaWhatsApp')}
                  </Button>

                  <Divider sx={{ my: 4 }} />

                  {/* Quick Info */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ color: 'var(--navy-blue)', opacity: 0.7 }}>{t('tourDetail.durationLabel')}</Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ color: 'var(--navy-blue)' }}>{t('days', { count: tour.duration })}</Typography>
                    </Box>
                    {tour.startDate && tour.endDate && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1" sx={{ color: 'var(--navy-blue)', opacity: 0.7 }}>{t('tourDetail.startDateLabel')}</Typography>
                          <Typography variant="body1" fontWeight={500} sx={{ color: 'var(--navy-blue)' }}>{formatDate(tour.startDate)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1" sx={{ color: 'var(--navy-blue)', opacity: 0.7 }}>{t('tourDetail.endDateLabel')}</Typography>
                          <Typography variant="body1" fontWeight={500} sx={{ color: 'var(--navy-blue)' }}>{formatDate(tour.endDate)}</Typography>
                        </Box>
                      </>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ color: 'var(--navy-blue)', opacity: 0.7 }}>{t('tourDetail.destinationLabel')}</Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ color: 'var(--navy-blue)' }}>{getTranslatedData(tour.destination, i18n.language)}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Booking Dialog */}
      <Dialog open={bookingDialogOpen} onClose={handleCloseBooking}>
        <DialogTitle sx={{ backgroundColor: 'var(--navy-blue)', color: 'white', fontWeight: 600 }}>
          {t('tourDetail.bookingModal.title', { tourName: getTranslatedData(tour?.name, i18n.language) })}
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important', mt: 2 }}>
           <Typography variant="h6" gutterBottom>
              {getTranslatedData(tour?.name, i18n.language)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
              {t('tourDetail.bookingModal.description', { date: tour ? formatDate(tour.startDate) : '' })}
            </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label={t('tourDetail.bookingModal.name')}
            type="text"
            fullWidth
            value={bookingForm.name}
            onChange={(e) => handleFormChange('name', e.target.value)}
          />
          <TextField
            margin="dense"
            id="email"
            label={t('tourDetail.bookingModal.email')}
            type="email"
            fullWidth
            value={bookingForm.email}
            onChange={(e) => handleFormChange('email', e.target.value)}
          />
          <TextField
            margin="dense"
            id="phone"
            label={t('tourDetail.bookingModal.phone')}
            type="tel"
            fullWidth
            value={bookingForm.phone}
            onChange={(e) => handleFormChange('phone', e.target.value)}
          />
          <TextField
            margin="dense"
            id="people"
            label={t('tourDetail.bookingModal.people')}
            type="number"
            fullWidth
            value={bookingForm.people}
            onChange={(e) => handleFormChange('people', parseInt(e.target.value, 10))}
            InputProps={{ inputProps: { min: 1, max: 10 } }}
          />
           <TextField
            margin="dense"
            id="specialRequests"
            label={t('tourDetail.bookingModal.specialRequests')}
            type="text"
            fullWidth
            multiline
            rows={3}
            value={bookingForm.specialRequests}
            onChange={(e) => handleFormChange('specialRequests', e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Button onClick={handleCloseBooking} sx={{ color: 'var(--navy-blue)' }}>{t('tourDetail.bookingModal.cancel')}</Button>
          <Button onClick={handleSubmitBooking} variant="contained" sx={{ 
            backgroundColor: 'var(--light-blue-cta)',
            '&:hover': { backgroundColor: 'var(--cobalt-blue)'}
          }}>
            {t('tourDetail.bookingModal.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      
      <WhatsAppButton />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TourDetailPage; 